import type {
  Unit, Pixel, Audio,
} from '@/data';
import { Source, SourceType } from '@/data';
import type { Image3DPageData } from '@/data/Source';
import type { MetadataFormat } from '@/data/Metadata';
import type Metadata from '@/data/Metadata';
import MetadataParser from '../MetadataParser';

/**
 * An image file object
 *
 * Contains:
 * * the relative path to an image file
 * * a time conversion object
 *
 * For score images, the conversion should be onset -> pixel
 *
 * For wave images, the conversion should be second -> pixel
 */
export interface FileFormat extends MetadataFormat{
  image: string;
  positions: string;
  type: SourceType;
  name?: string;
  source?: string;
  id?:string;
}

export interface ImageFileFormat extends FileFormat {
  type: SourceType.Score;
}

export interface Image3DFileFormat extends FileFormat {
  type: SourceType.Image3D;
}

export interface AudioImageFileFormat extends FileFormat {
  type: SourceType.AudioImage;
}

export interface ImageDimensions {
  height: Pixel,
  width: Pixel,
}

export function fileFormatFromNewFormat(sourceFormat : Map<string, unknown>) : FileFormat {
  let images : string[] = (sourceFormat.get('images') ?? []) as string[];
  if (images.length === 0 && sourceFormat.has('image')) {
    images = [sourceFormat.get('image') as string];
  }
  const defaultType : SourceType = SourceType.Image3D;

  const refs : Record<string, string> = {};

  (Array.from(sourceFormat.keys()) as string[]).filter((key) => key.startsWith('ref')).forEach((refKey) => { refs[refKey] = sourceFormat.get(refKey) as string; });

  return {
    image: images[0],
    positions: sourceFormat.get('positions') as string,
    type: defaultType,
    info: sourceFormat.get('text') as string,
    name: sourceFormat.get('name') as string,
    license: sourceFormat.get('license') as string,
    contributors: sourceFormat.get('contributors') as Record<string, string>,
    refs,
    source: sourceFormat.get('source') as string,
    year: sourceFormat.get('year') as string,
    id: sourceFormat.get('id') as string,
  };
}
export default class SourceParser extends Source {
  static fromFormat(
    format: FileFormat,
    dim: ImageDimensions,
    pixelUnit: Unit<Pixel>,
    imageUrl: string,
    track?: Audio,
    pages?: Array<Image3DPageData>,
  ): Source {
    try {
      const type = String(format.type || '') as SourceType;

      const name = String(format.name || '');

      const source = format.source || undefined;

      const metadata : Metadata = MetadataParser.fromFormat(format);

      return new Source({
        pixelUnit,
        imageUrl,
        type,
        name,
        track,
        pages,
        source,
        metadata,
        ...dim,
      });
    } catch (e) {
      const error = e as Error;
      throw new Error(`Corrupted Source: ${error.message}`);
    }
  }
}
